import { CheckCircleIcon } from "@phosphor-icons/react/dist/ssr";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/core/shared/v2/lib/utils";
import { Button } from "@/core/shared/v2/ui/button";

interface EditableSummaryCardProps {
  title: string;
  description?: string;
  icon: ReactNode;
  status: "empty" | "editing" | "completed";
  summary?: ReactNode;
  onEdit: () => void;
  children: ReactNode;
  disabled?: boolean;
  // Hide the edit/fill-in CTAs when the data is no longer mutable (e.g. after
  // successful registration on the backend — the orderer's record is committed
  // and editing it client-side would diverge from server state).
  lockEdit?: boolean;
  // Optional message shown below the header (above the collapsed body), e.g. a
  // "you need to fill these in" prompt triggered by clicking the page-level
  // submit CTA while the card is still empty. Hidden while the card is being edited.
  validationMessage?: ReactNode;
}

export const EditableSummaryCard = ({
  title,
  description,
  icon,
  status,
  summary,
  onEdit,
  children,
  disabled,
  lockEdit,
  validationMessage,
}: EditableSummaryCardProps) => {
  const { t } = useTranslation();
  return (
    <div
      className={cn(
        "flex w-full flex-col rounded-lg bg-white transition-all duration-300",
        status === "completed" ? "border-2 border-dodger-blue-700" : "",
        status === "empty" ? "border border-gray-200" : "",
        disabled && "pointer-events-none opacity-50",
      )}
    >
      {/* Header Section */}
      {status !== "editing" && (
        <div
          className={cn(
            // Mobile stacks the CTA below the title/description so the button can take
            // the full row width — putting it inline on a phone shrinks both sides too
            // much. md+ keeps the original side-by-side header.
            "flex flex-col gap-3 p-4 md:flex-row md:justify-between md:gap-2 lg:p-6",
            status === "empty" ? "md:items-center" : "md:items-start",
          )}
        >
          <div className="flex items-start gap-4">
            <div
              className={cn(
                "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-gray-200",
                status === "completed"
                  ? "border-none bg-dodger-blue-50 text-dodger-blue-700"
                  : "bg-gray-50 text-gray-500",
              )}
            >
              {status === "completed" ? <CheckCircleIcon size={24} weight="fill" /> : icon}
            </div>
            <div className="flex flex-col">
              <h3 className="font-serif text-xl lg:text-2xl">{title}</h3>
              {status === "empty" && description && <p className="mt-1 text-muted-foreground text-sm">{description}</p>}
              {status === "completed" && summary && <div className="mt-3 text-gray-700 text-sm">{summary}</div>}
            </div>
          </div>

          {status === "empty" && !lockEdit && (
            <Button
              variant="secondary"
              size="sm"
              className="w-full md:w-auto md:shrink-0"
              onClick={onEdit}
              disabled={disabled}
            >
              {t("VOUCHERS_DISTRIBUTION.CHECKOUT_CARD.FILL_IN_CTA")}
            </Button>
          )}

          {status === "completed" && !lockEdit && (
            <Button
              variant="secondary"
              size="sm"
              className="w-full md:w-auto md:shrink-0"
              onClick={onEdit}
              disabled={disabled}
            >
              {t("VOUCHERS_DISTRIBUTION.CHECKOUT_CARD.EDIT_CTA")}
            </Button>
          )}
        </div>
      )}

      {/* Validation banner sits between header and (hidden) content. Header keeps
          its bottom padding (p-4 / lg:p-6) so the banner appears spaced below the
          title row; the banner itself only needs side+bottom padding. */}
      {status !== "editing" && validationMessage && (
        <div className="px-4 pb-4 lg:px-6 lg:pb-6">{validationMessage}</div>
      )}

      {/* Content Section */}
      <div
        className={cn(
          status !== "editing" && "hidden",
          status === "editing" && "fade-in slide-in-from-top-2 animate-in fill-mode-both duration-300",
        )}
      >
        {children}
      </div>
    </div>
  );
};
